import {
  ArrowLeftRight,
  BarChart3,
  CalendarClock,
  CalendarDays,
  CalendarRange,
  CreditCard,
  Crosshair,
  FileBadge,
  FolderOpen,
  Landmark,
  LayoutGrid,
  LayoutList,
  LifeBuoy,
  ListTodo,
  PiggyBank,
  RefreshCw,
  Repeat,
  Settings,
  SlidersHorizontal,
  SquareKanban,
  Sun,
  Tags,
  Target,
  Upload,
  Users,
  Wallet,
  Wand2,
  type LucideIcon,
} from 'lucide-react';
import type { ProfileFeatures } from '@/types/planning';

// Menu lateral e títulos do header. Fica fora do DashboardLayout para o teste de cobertura da
// documentação (src/docs/docs.coverage.test.ts) conferir que TODA tela do menu tem documento.

export type BaseMenuItem = {
  label: string;
  icon: LucideIcon;
  key?: string;
  permission?: string | null;
  /** Visível se o usuário tiver QUALQUER uma destas permissões (OR). */
  anyPermission?: string[];
  /** Além da permissão, exige o módulo liberado no perfil (`features` do /auth/profile). */
  feature?: keyof ProfileFeatures;
};

export type LinkMenuItem = BaseMenuItem & {
  path: string;
  /** Ativo só na rota exata (ex.: "/" não acende em /kanban). */
  exact?: boolean;
};

export type ParentMenuItem = BaseMenuItem & {
  children: LinkMenuItem[];
};

export type MenuItem = LinkMenuItem | ParentMenuItem;

export const MENU_ITEMS: MenuItem[] = [
  { label: 'Hoje', icon: Sun, path: '/', permission: 'agenda.dashboard.read', exact: true },
  {
    label: 'Agenda',
    icon: LayoutList,
    key: 'agenda',
    anyPermission: ['agenda.activities.read', 'agenda.recurrences.manage', 'agenda.boards.manage'],
    children: [
      { label: 'Kanban', icon: SquareKanban, path: '/kanban', permission: 'agenda.activities.read' },
      { label: 'Calendário', icon: CalendarDays, path: '/calendar', permission: 'agenda.activities.read' },
      { label: 'Lista', icon: ListTodo, path: '/activities', permission: 'agenda.activities.read' },
      { label: 'Recorrências', icon: Repeat, path: '/recurrences', permission: 'agenda.recurrences.manage' },
      { label: 'Quadros', icon: LayoutGrid, path: '/boards', permission: 'agenda.boards.manage' },
    ],
  },
  { label: 'Documentos', icon: FolderOpen, path: '/documents', permission: 'documentos.read' },
  {
    label: 'Acervo CAC',
    icon: Target,
    key: 'cac',
    anyPermission: ['acervo.read'],
    feature: 'cac',
    children: [
      { label: 'Armas', icon: Crosshair, path: '/cac/firearms', permission: 'acervo.read', feature: 'cac' },
      { label: 'Documentos CAC', icon: FileBadge, path: '/cac/documents', permission: 'acervo.read', feature: 'cac' },
      { label: 'Vencimentos', icon: CalendarClock, path: '/cac/expiring', permission: 'acervo.read', feature: 'cac' },
    ],
  },
  {
    label: 'Finanças',
    icon: Wallet,
    key: 'financas',
    anyPermission: ['financas.read', 'financas.import'],
    children: [
      { label: 'Contas', icon: Landmark, path: '/finance/accounts', permission: 'financas.read' },
      { label: 'Cartões', icon: CreditCard, path: '/finance/cards', permission: 'financas.read' },
      { label: 'Lançamentos', icon: ArrowLeftRight, path: '/finance/transactions', permission: 'financas.read' },
      { label: 'Recorrências', icon: RefreshCw, path: '/finance/recurring', permission: 'financas.read' },
      { label: 'Projeção', icon: CalendarRange, path: '/finance/projection', permission: 'financas.read' },
      { label: 'Orçamento', icon: PiggyBank, path: '/finance/budget', permission: 'financas.read' },
      { label: 'Relatórios', icon: BarChart3, path: '/finance/reports', permission: 'financas.read' },
      { label: 'Categorias', icon: Tags, path: '/finance/categories', permission: 'financas.read' },
      { label: 'Importar', icon: Upload, path: '/finance/import', permission: 'financas.import' },
      { label: 'Regras de importação', icon: Wand2, path: '/finance/import-rules', permission: 'financas.import' },
    ],
  },
  {
    label: 'Administração',
    icon: Settings,
    key: 'admin',
    anyPermission: ['admin.users.manage', 'admin.settings.manage'],
    children: [
      { label: 'Usuários', icon: Users, path: '/admin/users', permission: 'admin.users.manage' },
      { label: 'Configurações', icon: SlidersHorizontal, path: '/admin/settings', permission: 'admin.settings.manage' },
    ],
  },
  { label: 'Central de ajuda', icon: LifeBuoy, path: '/help' },
];

/** Títulos do header por prefixo de rota (o mais específico primeiro; "/" por último). */
export const PAGE_TITLES: [string, string][] = [
  ['/kanban', 'Kanban'],
  ['/calendar', 'Calendário'],
  ['/activities', 'Atividades'],
  ['/recurrences', 'Recorrências'],
  ['/boards', 'Quadros'],
  ['/documents', 'Documentos'],
  ['/cac/firearms', 'Armas'],
  ['/cac/documents', 'Documentos CAC'],
  ['/cac/expiring', 'Vencimentos'],
  ['/finance/accounts', 'Contas'],
  ['/finance/cards', 'Cartões'],
  ['/finance/transactions', 'Lançamentos'],
  ['/finance/recurring', 'Recorrências'],
  ['/finance/projection', 'Projeção'],
  ['/finance/budget', 'Orçamento'],
  ['/finance/reports', 'Relatórios'],
  ['/finance/categories', 'Categorias'],
  ['/finance/import-rules', 'Regras de importação'],
  ['/finance/import', 'Importar extrato'],
  ['/admin/users', 'Usuários'],
  ['/admin/settings', 'Configurações'],
  ['/help', 'Central de ajuda'],
  ['/', 'Hoje'],
];

/** Todas as telas do menu (folhas), na ordem do menu. */
export const menuLeaves = (items: MenuItem[] = MENU_ITEMS): LinkMenuItem[] =>
  items.flatMap((i) => ('children' in i ? i.children : [i]));
